'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useAuthStore } from '@/store/useAuthStore';
import { api } from '@/lib/api';
import { HttpError } from '@/lib/api/adapters/httpClient';
import { useToast } from '@/components/ui/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useCooldown } from '@/lib/hooks/useCooldown';

export function LoginPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { login } = useAuthStore();
  const { addToast } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showVerificationPrompt, setShowVerificationPrompt] = useState(false);
  const [resending, setResending] = useState(false);
  const { remaining, start, isActive } = useCooldown(60);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      addToast({ title: 'Алдаа', description: 'Имэйл болон нууц үг оруулна уу', variant: 'destructive' });
      return;
    }
    setLoading(true);
    setShowVerificationPrompt(false);
    try {
      await login(email.trim(), password);
      const redirect = searchParams.get('redirect') || '/';
      router.push(redirect);
      addToast({ title: 'Амжилттай', description: 'Амжилттай нэвтэрлээ' });
    } catch (error: any) {
      // Check if it's a 403 email verification error
      if (error instanceof HttpError && error.status === 403) {
        setShowVerificationPrompt(true);
      } else if (error.message?.includes('verify your email') || error.message?.includes('Please verify')) {
        setShowVerificationPrompt(true);
      } else {
        // Regular login error
        const message = error.message || 'Нэвтрэхэд алдаа гарлаа';
        addToast({ title: 'Алдаа', description: message, variant: 'destructive' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email.trim()) {
      addToast({
        title: 'Алдаа',
        description: 'Имэйл хаягаа оруулна уу',
        variant: 'destructive',
      });
      return;
    }

    setResending(true);
    try {
      await api.auth.resendVerification(email.trim());
      start(); // Start cooldown timer
      addToast({
        title: 'Имэйл илгээсэн',
        description: 'Хэрэв бүртгэл байвал баталгаажуулах имэйл илгээсэн',
      });
    } catch (err: any) {
      addToast({
        title: 'Алдаа',
        description: err.message || 'Баталгаажуулах имэйл илгээхэд алдаа гарлаа',
        variant: 'destructive',
      });
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
      <div className="flex justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-2xl">NomadShop-д нэвтрэх</CardTitle>
            <CardDescription>
              Имэйл болон нууц үгээ оруулна уу
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-2">
                  Имэйл
                </label>
                <Input
                  id="email"
                  data-testid="login-email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium mb-2">
                  Нууц үг
                </label>
                <Input
                  id="password"
                  data-testid="login-password"
                  type="password"
                  placeholder="Нууц үгээ оруулна уу"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" data-testid="login-submit" className="w-full" disabled={loading}>
                {loading ? 'Нэвтэрч байна...' : 'Нэвтрэх'}
              </Button>
              <div className="text-center space-y-2">
                <div>
                  <Link href="/forgot-password" className="text-sm text-blue-600 hover:underline">
                    Нууц үг мартсан уу?
                  </Link>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Бүртгэл байхгүй юу? </span>
                  <Link href="/register" className="text-sm text-blue-600 hover:underline">
                    Бүртгүүлэх
                  </Link>
                </div>
              </div>
            </form>

            {showVerificationPrompt && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900 mb-1">
                      Таны имэйл хараахан баталгаажаагүй байна
                    </p>
                    <p className="text-sm text-gray-600 mb-3">
                      Нэвтрэхийн өмнө имэйл хаягаа баталгаажуулна уу. Баталгаажуулах холбоосыг имэйлээс шалгана уу.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={handleResendVerification}
                    disabled={resending || isActive}
                  >
                    {resending
                      ? 'Илгээж байна...'
                      : isActive
                      ? `Баталгаажуулах имэйл дахин илгээх (${remaining}с)`
                      : 'Баталгаажуулах имэйл дахин илгээх'}
                  </Button>
                  <Link href={`/check-email?email=${encodeURIComponent(email.trim())}`} className="block">
                    <Button variant="ghost" className="w-full text-sm">
                      Имэйл шалгах хуудас руу очих
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
